import { useMemo } from "react";
import { useGetEnergyType } from "../../../../../../../api/common";
import { useGetFacilityById } from "../../../../../../../api/facility";
import {
  useGetCountries,
  useGetHeatSourceType,
  useGetRefrigerantSourceType,
  useGetWaterSourceType
} from "../../../../../../../api/static-data";
import { ExternalStorageToolVariant } from "../../externalStorage.types";
import { buildFacilityViewModel } from "../externalStorageFacility.utils";

interface UseExternalStorageFacilityViewParams {
  facilityId: string;
  variant?: ExternalStorageToolVariant;
  isOpen: boolean;
}

export const useExternalStorageFacilityView = ({
  facilityId,
  variant = "mapping",
  isOpen
}: UseExternalStorageFacilityViewParams) => {
  const { data: countries, isLoading: isCountriesLoading } = useGetCountries();
  const { data: facilityData, isFetching: isFacilityLoading } = useGetFacilityById(
    isOpen ? facilityId : ""
  );

  const isSimulatedFacility = Boolean(facilityData?.isSimulatedFacility);
  const showOriginalPanel = variant === "simulate" && isSimulatedFacility;

  const { data: originalFacilityData, isFetching: isOriginalFacilityLoading } = useGetFacilityById(
    isOpen && showOriginalPanel ? facilityData?.originalId || "" : ""
  );

  const { data: energyTypes, isLoading: isEnergyTypesLoading } = useGetEnergyType();
  const { data: heatTypes, isLoading: isHeatTypesLoading } = useGetHeatSourceType();
  const { data: waterTypes, isLoading: isWaterTypesLoading } = useGetWaterSourceType();
  const { data: refrigerantTypes, isLoading: isRefrigerantTypesLoading } =
    useGetRefrigerantSourceType();

  const viewModel = useMemo(
    () =>
      buildFacilityViewModel({
        facility: facilityData,
        originalFacility: originalFacilityData,
        countries,
        energyTypes,
        heatTypes,
        waterTypes,
        refrigerantTypes,
        showOriginalPanel
      }),
    [
      countries,
      energyTypes,
      facilityData,
      heatTypes,
      originalFacilityData,
      refrigerantTypes,
      showOriginalPanel,
      waterTypes
    ]
  );

  const isLoading =
    isCountriesLoading ||
    isFacilityLoading ||
    (showOriginalPanel && isOriginalFacilityLoading) ||
    isEnergyTypesLoading ||
    isHeatTypesLoading ||
    isWaterTypesLoading ||
    isRefrigerantTypesLoading;

  return {
    viewModel,
    isLoading,
    showOriginalPanel
  };
};
